import {
  createAccountSecrets,
  KDF_ITERATIONS,
  newSalt,
  unlockKey,
  type EncryptedData,
} from '../auth/crypto';

export interface AccountRecord {
  id: string;
  salt: string;
  iterations: number;
  verifier: EncryptedData;
  createdAt: number;
  lastUsedAt: number;
}

/** Stockage synchrone de l'index public (localStorage en production). */
export interface SyncStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Stockage des données chiffrées (IndexedDB en production). */
export interface BlobStore {
  put(id: string, blob: EncryptedData): Promise<void>;
  get(id: string): Promise<EncryptedData | null>;
  remove(id: string): Promise<void>;
}

const INDEX_KEY = 'terminal.nsi:accounts';
const LOCKOUT_KEY = 'terminal.nsi:lockouts';
const MIN_PASSWORD_LENGTH = 8;
const ID_PATTERN = /^[a-z0-9._-]{3,20}$/;
const RESERVED_IDS = new Set([
  'root',
  'admin',
  'administrateur',
  'invite',
  'invité',
  'guest',
  'system',
  'sudo',
  'nsi',
  'terminal',
]);

export interface IdValidation {
  normalized: string;
  error: string | null;
}

export function validateAccountId(id: string): IdValidation {
  const normalized = id.trim().toLowerCase();
  if (!ID_PATTERN.test(normalized)) {
    return {
      normalized,
      error: 'Identifiant invalide : 3 à 20 caractères parmi a-z, 0-9, point, tiret et underscore.',
    };
  }
  if (RESERVED_IDS.has(normalized)) {
    return { normalized, error: 'Cet identifiant est réservé.' };
  }
  return { normalized, error: null };
}

export function validatePassword(password: string): string | null {
  if (password.length < MIN_PASSWORD_LENGTH) {
    return `Mot de passe trop court : ${MIN_PASSWORD_LENGTH} caractères minimum.`;
  }
  return null;
}

interface LockoutEntry {
  failures: number;
  until: number;
}

export class Accounts {
  constructor(
    private readonly storage: SyncStorage,
    private readonly blobs: BlobStore,
    private readonly now: () => number = Date.now,
    private readonly iterations: number = KDF_ITERATIONS,
  ) {}

  list(): AccountRecord[] {
    const raw = this.storage.getItem(INDEX_KEY);
    if (!raw) return [];
    try {
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as AccountRecord[]) : [];
    } catch {
      return [];
    }
  }

  exists(id: string): boolean {
    const normalized = id.trim().toLowerCase();
    return this.list().some((record) => record.id === normalized);
  }

  async create(id: string, password: string): Promise<{ record: AccountRecord; key: CryptoKey }> {
    const validation = validateAccountId(id);
    if (validation.error) throw new Error(validation.error);
    const passwordError = validatePassword(password);
    if (passwordError) throw new Error(passwordError);
    if (this.exists(validation.normalized)) throw new Error('Ce compte existe déjà.');

    const salt = newSalt();
    const { key, verifier } = await createAccountSecrets(password, salt, this.iterations);
    const record: AccountRecord = {
      id: validation.normalized,
      salt,
      iterations: this.iterations,
      verifier,
      createdAt: this.now(),
      lastUsedAt: this.now(),
    };
    this.saveIndex([...this.list(), record]);
    return { record, key };
  }

  async login(
    id: string,
    password: string,
  ): Promise<{ record: AccountRecord; key: CryptoKey } | null> {
    const normalized = id.trim().toLowerCase();
    const record = this.list().find((entry) => entry.id === normalized);
    if (!record) return null;

    const remaining = this.lockedUntil(normalized);
    if (remaining > 0) {
      throw new Error(
        `Trop de tentatives : réessayez dans ${Math.ceil(remaining / 1000)} seconde(s).`,
      );
    }

    const key = await unlockKey(password, record.salt, record.iterations, record.verifier);
    if (!key) {
      this.recordFailure(normalized);
      return null;
    }

    this.clearFailures(normalized);
    record.lastUsedAt = this.now();
    this.saveIndex(this.list().map((entry) => (entry.id === normalized ? record : entry)));
    return { record, key };
  }

  async remove(id: string): Promise<void> {
    const normalized = id.trim().toLowerCase();
    this.saveIndex(this.list().filter((record) => record.id !== normalized));
    await this.blobs.remove(normalized);
  }

  /** Accès aux données chiffrées du compte. */
  getBlob(id: string): Promise<EncryptedData | null> {
    return this.blobs.get(id.trim().toLowerCase());
  }

  putBlob(id: string, blob: EncryptedData): Promise<void> {
    return this.blobs.put(id.trim().toLowerCase(), blob);
  }

  /** Importe un compte depuis un fichier `.nsi-backup` (enregistrement + données). */
  async importRecord(record: AccountRecord, blob: EncryptedData): Promise<void> {
    this.saveIndex([
      ...this.list().filter((entry) => entry.id !== record.id),
      { ...record, id: record.id.trim().toLowerCase() },
    ]);
    await this.blobs.put(record.id.trim().toLowerCase(), blob);
  }

  lockedUntil(id: string): number {
    const entry = this.loadLockouts()[id];
    if (!entry) return 0;
    return Math.max(0, entry.until - this.now());
  }

  private recordFailure(id: string): void {
    const lockouts = this.loadLockouts();
    const entry = lockouts[id] ?? { failures: 0, until: 0 };
    entry.failures += 1;
    const delay = entry.failures >= 5 ? 30_000 * 2 ** Math.min(entry.failures - 5, 4) : 0;
    entry.until = this.now() + delay;
    lockouts[id] = entry;
    this.storage.setItem(LOCKOUT_KEY, JSON.stringify(lockouts));
  }

  private clearFailures(id: string): void {
    const lockouts = this.loadLockouts();
    delete lockouts[id];
    this.storage.setItem(LOCKOUT_KEY, JSON.stringify(lockouts));
  }

  private loadLockouts(): Record<string, LockoutEntry> {
    const raw = this.storage.getItem(LOCKOUT_KEY);
    if (!raw) return {};
    try {
      const parsed: unknown = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, LockoutEntry>) : {};
    } catch {
      return {};
    }
  }

  private saveIndex(records: readonly AccountRecord[]): void {
    this.storage.setItem(INDEX_KEY, JSON.stringify(records));
  }
}

/** Adaptateur localStorage avec garde-fou (modes privés, quota…). */
export class WebStorageAdapter implements SyncStorage {
  getItem(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  setItem(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* stockage indisponible */
    }
  }

  removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      /* stockage indisponible */
    }
  }
}
